import {
  DEFAULT_LIVE_NOTIFICATION_PREFERENCES,
  normaliseLiveNotificationPreferences
} from './notification-preferences.js';
import {createLiveNotificationPlan} from './notification-plan.js';

function requireAdapter(adapter){
  if(!adapter?.reconcile||!adapter?.cancelOwned||!adapter?.ensurePermission){
    throw new TypeError('local notification adapter is required');
  }
  return adapter;
}

export function createLiveNotificationScheduler({
  adapter,
  preferenceStore,
  stageTargets,
  now=Date.now
}={}){
  const notifications=requireAdapter(adapter);
  if(!preferenceStore?.get||!preferenceStore?.save){
    throw new TypeError('preferenceStore is required');
  }
  if(!stageTargets?.getStageTarget){
    throw new TypeError('stageTargets.getStageTarget is required');
  }

  async function getPreferences(){
    return normaliseLiveNotificationPreferences(await preferenceStore.get());
  }

  async function enable(changes={}){
    const permission=await notifications.ensurePermission({request:true});
    if(permission.status!=='granted'){
      await preferenceStore.save({
        ...DEFAULT_LIVE_NOTIFICATION_PREFERENCES,
        ...changes,
        enabled:false
      });
      await notifications.cancelOwned();
      return Object.freeze({
        status:permission.supported===false?'unsupported':'permission_denied',
        permission:permission.status,
        preferences:await getPreferences()
      });
    }

    const preferences=await preferenceStore.save({...changes,enabled:true});
    return Object.freeze({
      status:'enabled',
      permission:'granted',
      preferences
    });
  }

  async function configure(changes={}){
    const current=await getPreferences();
    if(changes.enabled===true&&current.enabled!==true){
      return enable(changes);
    }

    const preferences=await preferenceStore.save({...current,...changes});
    if(preferences.enabled!==true){
      const result=await notifications.cancelOwned();
      return Object.freeze({status:'disabled',preferences,...result});
    }
    return Object.freeze({status:'configured',preferences});
  }

  async function disable(){
    const preferences=await preferenceStore.save({enabled:false});
    const result=await notifications.cancelOwned();
    return Object.freeze({status:'disabled',preferences,...result});
  }

  async function reconcileActiveSession({session}={}){
    const preferences=await getPreferences();
    if(preferences.enabled!==true){
      const result=await notifications.cancelOwned();
      return Object.freeze({status:'disabled',preferences,...result});
    }

    if(!session||session.status!=='smoking'||!session.activeStageId){
      const result=await notifications.cancelOwned();
      return Object.freeze({status:'no_active_session',preferences,...result});
    }

    const stageTarget=await stageTargets.getStageTarget(session.activeStageId);
    const desired=createLiveNotificationPlan({
      session,
      stageTarget,
      preferences,
      now
    });
    const result=await notifications.reconcile(desired);
    return Object.freeze({
      ...result,
      preferences,
      desiredCount:desired.length
    });
  }

  async function onInteraction(listener){
    if(typeof notifications.onAction!=='function'){
      return Object.freeze({remove:async()=>{}});
    }
    return notifications.onAction(listener);
  }

  async function clear(){
    return notifications.cancelOwned();
  }

  return Object.freeze({
    getPreferences,
    enable,
    configure,
    disable,
    reconcileActiveSession,
    onInteraction,
    clear
  });
}
